import _isEmpty from 'lodash/isEmpty';
import _uniqBy from 'lodash/uniqBy';
import type { AnyAction, Reducer } from 'redux';

import type { ServiceError, TableViewProps } from '@models';
import type { NotificationEndpointConfig } from '@models/events.models';
import {
  EVENTS_TABLE_PAGE_SIZE,
  MAX_ACCUMULATED_EVENTS,
} from '@features/events/events.const';

import {
  SET_EVENTS_PENDING,
  SET_EVENTS_SUCCESS,
  SET_EVENTS_ERROR,
  SET_EVENTS_VIEW_PROPERTIES,
  SET_EVENTS_ACTIVE_TAB,
  SET_NOTIFICATIONS_ACTIVE_TAB,
  SET_EVENTS_REMOVE_DIALOG,
  SET_EVENT_REMOVE_DIALOG,
  SET_EVENTS_REMOVE_PENDING,
  SET_EVENTS_REMOVE_SUCCESS,
  SET_EVENTS_REMOVE_ERROR,
  CLEAR_EVENTS_REMOVE_ERRORS,
  CLEAR_EVENTS_PENDING,
  CLEAR_EVENTS_SUCCESS,
  CLEAR_EVENTS_ERROR,
  SET_EVENT_REMOVE_PENDING,
  SET_EVENT_REMOVE_SUCCESS,
  SET_EVENT_REMOVE_ERROR,
  SET_NEW_NOTIFICATION_CONFIG_TEST_PENDING,
  SET_NEW_NOTIFICATION_CONFIG_TEST_SUCCESS,
  SET_NEW_NOTIFICATION_CONFIG_TEST_ERROR,
  SET_NOTIFICATIONS_SPEC_FETCH_PENDING,
  SET_NOTIFICATIONS_SPEC_FETCH_SUCCESS,
  SET_NOTIFICATIONS_SPEC_FETCH_ERROR,
  SET_NOTIFICATION_ADD_DIALOG,
  SET_NOTIFICATION_UPDATE_DIALOG,
  SET_NOTIFICATION_REMOVE_DIALOG,
  SET_NOTIFICATION_CONFIGS_FETCH_PENDING,
  SET_NOTIFICATION_CONFIGS_FETCH_SUCCESS,
  SET_NOTIFICATION_CONFIGS_FETCH_ERROR,
  SET_NOTIFICATION_CONFIG_TEST_PENDING,
  SET_NOTIFICATION_CONFIG_TEST_SUCCESS,
  SET_NOTIFICATION_CONFIG_TEST_ERROR,
  SET_NOTIFICATION_CONFIG_ADD_PENDING,
  SET_NOTIFICATION_CONFIG_ADD_SUCCESS,
  SET_NOTIFICATION_CONFIG_ADD_ERROR,
  SET_NOTIFICATION_CONFIG_UPDATE_PENDING,
  SET_NOTIFICATION_CONFIG_UPDATE_SUCCESS,
  SET_NOTIFICATION_CONFIG_UPDATE_ERROR,
  SET_NOTIFICATION_CONFIG_REMOVE_PENDING,
  SET_NOTIFICATION_CONFIG_REMOVE_SUCCESS,
  SET_NOTIFICATION_CONFIG_REMOVE_ERROR,
  SET_EVENTS_SELECTED,
  SET_EVENTS_SEARCH_PENDING,
  SET_EVENTS_SEARCH_SUCCESS,
  SET_EVENTS_SEARCH_ERROR,
  CLEAR_EVENTS_SEARCH,
} from '../action-types';

interface Event {
  event: {
    resource: {
      account_name: string;
      type: string;
      id: string;
    };
    source: {
      base_url: string;
      request_id: string | null;
      service_name: string;
      host_id: string;
    };
    level: string;
    details: {
      msg?: string;
      [key: string]: any;
    };
    type: string;
    message: string;
    timestamp: string;
  };
  event_id: string;
  created_at: string;
}

interface EventData {
  results: Event[];
  next_page: string | null;
  item_count: number;
  page: number;
}

interface ViewProps {
  events: {
    filterStr: string;
    page: number;
    pageSize: number;
    filterLevel: {
      info: boolean;
      error: boolean;
    };
    selectOption: 'custom' | 'all' | 'retrieved';
  };
  notifications: TableViewProps;
}

export interface EventsState {
  activeTabIndex: number;
  activeNotificationsTabIndex: number;
  isFetchingEvents: boolean;
  fetchEventsError: boolean;
  data: EventData | false;
  isRemoveEventsModalOpen: boolean;
  isRemoveEventModalOpen: boolean;
  isRemoving: boolean;
  removeError: boolean;
  isRemovingEvents: boolean;
  removeEventsError: boolean;
  removeEventsErrors: { [key: string]: any } | false;
  isSearchingEvents: boolean;
  searchEventsError: boolean;
  filteredEvents: EventData | false;
  isAddNotificationModalOpen: string | false;
  isUpdateNotificationModalOpen: string | false;
  isRemoveNotificationModalOpen: string | false;
  isFetchingNotificationConfigs: boolean;
  fetchNotificationConfigsError: boolean;
  notificationConfigs: NotificationEndpointConfig[] | false;
  isTestingNewNotificationConfig: boolean | string;
  testNewNotificationConfigError: boolean;
  newNotificationTest: any | false;
  isTestingNotificationConfig: boolean | string;
  testNotificationConfigError: boolean;
  notificationTest: any | false;
  isAddingNotificationConfig: boolean | string;
  addNotificationConfigError: boolean;
  isUpdatingNotificationConfig: boolean | string;
  updateNotificationConfigError: boolean;
  isRemovingNotificationConfig: boolean | string;
  removeNotificationConfigError: boolean;
  isFetchingNotificationsSpec: boolean;
  fetchNotificationsSpecError: boolean;
  notificationsSpec: Record<string, unknown> | false;
  errorMsg: ServiceError | null;
  before: string | false;
  since: string | false;
  selected: { [key: string]: boolean };
  viewProps: ViewProps;
  status?: string;
}

const defaultState: EventsState = {
  activeTabIndex: 0,
  activeNotificationsTabIndex: 0,

  isFetchingEvents: false,
  fetchEventsError: false,
  data: false,

  isRemoveEventsModalOpen: false,
  isRemoveEventModalOpen: false,
  isRemoving: false,
  removeError: false,
  isRemovingEvents: false,
  removeEventsError: false,
  removeEventsErrors: false,

  isSearchingEvents: false,
  searchEventsError: false,
  filteredEvents: false,

  isAddNotificationModalOpen: false,
  isUpdateNotificationModalOpen: false,
  isRemoveNotificationModalOpen: false,

  isFetchingNotificationConfigs: false,
  fetchNotificationConfigsError: false,
  notificationConfigs: false,

  isTestingNewNotificationConfig: false,
  testNewNotificationConfigError: false,
  newNotificationTest: false,

  isTestingNotificationConfig: false,
  testNotificationConfigError: false,
  notificationTest: false,

  isAddingNotificationConfig: false,
  addNotificationConfigError: false,

  isUpdatingNotificationConfig: false,
  updateNotificationConfigError: false,

  isRemovingNotificationConfig: false,
  removeNotificationConfigError: false,

  // Notifications Swagger Spec Props
  isFetchingNotificationsSpec: false,
  fetchNotificationsSpecError: false,
  notificationsSpec: false,

  errorMsg: null,

  before: false,
  since: false,
  selected: {},
  viewProps: {
    events: {
      filterStr: '',
      page: 0,
      pageSize: EVENTS_TABLE_PAGE_SIZE,
      filterLevel: {
        info: true,
        error: true,
      },
      selectOption: 'custom',
    },
    notifications: {
      filterStr: '',
      page: 0,
      pageSize: EVENTS_TABLE_PAGE_SIZE,
    },
  },
};

const reducer: Reducer<EventsState> = (
  state = defaultState,
  action: AnyAction,
) => {
  switch (action.type) {
    /**
     * *************************************************************************
     * NOTIFICATIONS SPEC DATA REDUCERS
     */
    case SET_NOTIFICATIONS_SPEC_FETCH_PENDING:
      return {
        ...state,
        isFetchingNotificationsSpec: true,
      };
    case SET_NOTIFICATIONS_SPEC_FETCH_SUCCESS: {
      return {
        ...state,
        isFetchingNotificationsSpec: false,
        fetchNotificationsSpecError: false,
        notificationsSpec: action.resp.data,
      };
    }
    case SET_NOTIFICATIONS_SPEC_FETCH_ERROR:
      return {
        ...state,
        isFetchingNotificationsSpec: false,
        fetchNotificationsSpecError: true,
        errorMsg: action.errorMsg,
      };

    /**
     * *************************************************************************
     * NOTIFICATIONS VIEW REDUCERS
     */
    case SET_NOTIFICATION_ADD_DIALOG:
      return {
        ...state,
        isAddNotificationModalOpen: action.open,
        addNotificationConfigError: false,
        errorMsg: null,
      };

    case SET_NOTIFICATION_UPDATE_DIALOG:
      return {
        ...state,
        isUpdateNotificationModalOpen: action.id,
        updateNotificationConfigError: false,
        errorMsg: null,
      };

    case SET_NOTIFICATION_REMOVE_DIALOG:
      return {
        ...state,
        isRemoveNotificationModalOpen: action.id,
        removeNotificationConfigError: false,
        errorMsg: null,
      };

    /**
     * *************************************************************************
     * NOTIFICATIONS DATA REDUCERS
     */
    case SET_NOTIFICATION_CONFIGS_FETCH_PENDING:
      return {
        ...state,
        isFetchingNotificationConfigs: true,
      };
    case SET_NOTIFICATION_CONFIGS_FETCH_SUCCESS:
      return {
        ...state,
        isFetchingNotificationConfigs: false,
        fetchNotificationConfigsError: false,
        errorMsg: null,
        notificationConfigs: action.resp.data,
      };
    case SET_NOTIFICATION_CONFIGS_FETCH_ERROR:
      return {
        ...state,
        isFetchingNotificationConfigs: false,
        fetchNotificationConfigsError: true,
        errorMsg: action.errorMsg,
        notificationConfigs: false,
      };

    case SET_NOTIFICATION_CONFIG_ADD_PENDING:
      return {
        ...state,
        isAddingNotificationConfig: action.id,
      };
    case SET_NOTIFICATION_CONFIG_ADD_SUCCESS:
      return {
        ...state,
        isAddingNotificationConfig: false,
        addNotificationConfigError: false,
        isAddNotificationModalOpen: false,
        errorMsg: null,
      };
    case SET_NOTIFICATION_CONFIG_ADD_ERROR:
      return {
        ...state,
        isAddingNotificationConfig: false,
        addNotificationConfigError: true,
        errorMsg: action.errorMsg,
      };

    case SET_NOTIFICATION_CONFIG_UPDATE_PENDING:
      return {
        ...state,
        isUpdatingNotificationConfig: action.id,
      };
    case SET_NOTIFICATION_CONFIG_UPDATE_SUCCESS:
      return {
        ...state,
        isUpdatingNotificationConfig: false,
        updateNotificationConfigError: false,
        isUpdateNotificationModalOpen: false,
        errorMsg: null,
      };
    case SET_NOTIFICATION_CONFIG_UPDATE_ERROR:
      return {
        ...state,
        isUpdatingNotificationConfig: false,
        updateNotificationConfigError: true,
        errorMsg: action.errorMsg,
      };

    case SET_NOTIFICATION_CONFIG_REMOVE_PENDING:
      return {
        ...state,
        isRemovingNotificationConfig: action.id,
      };
    case SET_NOTIFICATION_CONFIG_REMOVE_SUCCESS:
      return {
        ...state,
        isRemovingNotificationConfig: false,
        removeNotificationConfigError: false,
        isRemoveNotificationsModalOpen: false,
        errorMsg: null,
      };
    case SET_NOTIFICATION_CONFIG_REMOVE_ERROR:
      return {
        ...state,
        isRemovingNotificationConfig: false,
        removeNotificationConfigError: true,
        errorMsg: action.errorMsg,
      };

    /**
     * *************************************************************************
     * NEW NOTIFICATION TEST REDUCERS
     */
    case SET_NEW_NOTIFICATION_CONFIG_TEST_PENDING:
      return {
        ...state,
        isTestingNewNotificationConfig: action.id,
        newNotificationTest: false,
      };
    case SET_NEW_NOTIFICATION_CONFIG_TEST_SUCCESS:
      return {
        ...state,
        isTestingNewNotificationConfig: false,
        testNewNotificationConfigError: false,
        errorMsg: null,
        newNotificationTest: action.resp.data,
      };
    case SET_NEW_NOTIFICATION_CONFIG_TEST_ERROR:
      return {
        ...state,
        isTestingNewNotificationConfig: false,
        testNewNotificationConfigError: true,
        errorMsg: action.errorMsg,
        newNotificationTest: false,
      };

    /**
     * *************************************************************************
     * NOTIFICATIONS TEST REDUCERS
     */
    case SET_NOTIFICATION_CONFIG_TEST_PENDING:
      return {
        ...state,
        isTestingNotificationConfig: action.id,
        notificationTest: false,
      };
    case SET_NOTIFICATION_CONFIG_TEST_SUCCESS:
      return {
        ...state,
        isTestingNotificationConfig: false,
        testNotificationConfigError: false,
        errorMsg: null,
        notificationTest: action.resp.data,
      };
    case SET_NOTIFICATION_CONFIG_TEST_ERROR:
      return {
        ...state,
        isTestingNotificationConfig: false,
        testNotificationConfigError: true,
        errorMsg: action.errorMsg,
        notificationTest: false,
      };

    /**
     * *************************************************************************
     * EVENT VIEW REDUCERS
     */
    case SET_EVENTS_ACTIVE_TAB:
      return {
        ...state,
        activeTabIndex: action.idx,
      };

    case SET_NOTIFICATIONS_ACTIVE_TAB:
      return {
        ...state,
        activeNotificationsTabIndex: action.idx,
      };

    case SET_EVENTS_VIEW_PROPERTIES:
      return {
        ...state,
        viewProps: action.viewProps,
      };

    case SET_EVENTS_REMOVE_DIALOG:
      return {
        ...state,
        isRemoveEventsModalOpen: action.open,
        errorMsg: null,
      };

    case SET_EVENT_REMOVE_DIALOG:
      return {
        ...state,
        isRemoveEventModalOpen: action.open,
        removeError: false,
        errorMsg: null,
      };

    case SET_EVENTS_SELECTED:
      return {
        ...state,
        selected: action.selected || {},
      };

    /**
     * *************************************************************************
     * EVENT DATA REDUCERS
     */
    case SET_EVENTS_PENDING:
      return {
        ...state,
        isFetchingEvents: !action.silent,
      };
    case SET_EVENTS_SUCCESS: {
      let stateCopy = { ...state };
      if (
        stateCopy.data &&
        stateCopy.data.results instanceof Array &&
        action.resp &&
        action.resp.data &&
        action.resp.data.page > 1 &&
        action.resp.data.results instanceof Array
      ) {
        stateCopy.data.results = stateCopy.data.results.concat(
          action.resp.data.results,
        );

        // Remove any duplicate events that may have crept into the
        // dataset by being pushed across page boundaries
        stateCopy.data.results = _uniqBy(stateCopy.data.results, 'event_id');

        // Cap accumulated results to prevent unbounded memory growth
        if (stateCopy.data.results.length > MAX_ACCUMULATED_EVENTS) {
          stateCopy.data.results = stateCopy.data.results.slice(
            -MAX_ACCUMULATED_EVENTS,
          );
        }
      } else {
        stateCopy = Object.assign(stateCopy, action.resp);
      }

      if (stateCopy.data) {
        stateCopy.data.next_page = action.resp.data.next_page;
        stateCopy.data.page = action.resp.data.page;
      }

      stateCopy.before = action.before;
      stateCopy.since = action.since;
      stateCopy.isFetchingEvents = false;
      stateCopy.fetchEventsError = false;
      stateCopy.errorMsg = null;
      return stateCopy;
    }
    case SET_EVENTS_ERROR:
      return {
        ...state,
        isFetchingEvents: false,
        fetchEventsError: true,
        errorMsg: action.errorMsg,
        data: false,
      };

    case SET_EVENTS_REMOVE_PENDING:
      return {
        ...state,
        isRemovingEvents: true,
      };
    case SET_EVENTS_REMOVE_SUCCESS: {
      const stateCopy = { ...state };
      const { results, errors } = action.resp.data as {
        results: string[];
        errors: { code: string; id: string }[];
      };
      const { data: newData, selected: newSelected = {} } = stateCopy;

      const checkForRemoval = (arr: Event[], currEvent: Event) => {
        const { event_id: id } = currEvent;
        const isSelected = !!newSelected[id];
        let keepEvent = true;
        // Don't keep if event was deleted / not found
        if (
          results.find(result => result === id) ||
          errors.find(err => err.code === 'NotFound' && err.id === id)
        ) {
          keepEvent = false;
        }
        // Remove selection of event if we're not keeping it
        if (!keepEvent && isSelected) delete newSelected[id];
        if (keepEvent) arr.push(currEvent);
        return arr;
      };

      if (newData && newData.results) {
        // Update state for most recent data
        newData.results = newData.results.reduce(checkForRemoval, []);
      }

      stateCopy.data = newData;
      // Ensure events are removed from filtered data (if available)
      if (stateCopy.filteredEvents) {
        const { filteredEvents: newFiltered } = stateCopy;
        // Update state for filtered data
        newFiltered.results = newFiltered.results.reduce(checkForRemoval, []);
        stateCopy.filteredEvents = newFiltered;
      }
      const newErrors = errors.reduce(
        (errs, currErr) => {
          const { id, code } = currErr;
          const newErrs = { ...errs };
          if (code !== 'NotFound') newErrs[id] = currErr;
          return newErrs;
        },
        {} as Record<string, { code: string; id: string }>,
      );
      const hasErrors = !_isEmpty(newErrors);
      return {
        ...stateCopy,
        isRemovingEvents: false,
        removeEventsError: hasErrors,
        removeEventsErrors: hasErrors ? newErrors : false,
        errorMsg: null,
        isRemoveEventsModalOpen: hasErrors,
        selected: newSelected,
      };
    }
    case SET_EVENTS_REMOVE_ERROR:
      return {
        ...state,
        isRemovingEvents: false,
        removeEventsError: true,
        errorMsg: action.errorMsg,
      };
    case CLEAR_EVENTS_REMOVE_ERRORS:
      return {
        ...state,
        removeEventsError: false,
        removeEventsErrors: false,
        isRemoveEventsModalOpen: false,
      };

    case CLEAR_EVENTS_PENDING:
      return {
        ...state,
        isRemovingEvents: true,
      };
    case CLEAR_EVENTS_SUCCESS: {
      return {
        ...state,
        isRemovingEvents: false,
        removeEventsError: false,
        errorMsg: null,
        selected: {},
        isRemoveEventsModalOpen: false,
      };
    }
    case CLEAR_EVENTS_ERROR:
      return {
        ...state,
        isRemovingEvents: false,
        removeEventsError: true,
        errorMsg: action.errorMsg,
      };

    case SET_EVENT_REMOVE_PENDING:
      return {
        ...state,
        isRemoving: true,
      };
    case SET_EVENT_REMOVE_SUCCESS: {
      const obj = {
        ...state,
        isRemoveEventModalOpen: false,
        isRemoving: false,
        removeError: false,
        errorMsg: null,
      };
      const id = action.resp.data;
      if (obj.data) {
        // Remove event from recent data
        obj.data.results = obj.data.results.filter(
          item => item.event_id !== id,
        );
      }
      // If filter data is available, remove event from that list as well
      if (obj.filteredEvents) {
        obj.filteredEvents.results = obj.filteredEvents.results.filter(
          item => item.event_id !== id,
        );
      }
      if (obj.selected && obj.selected[id]) {
        delete obj.selected[id];
      }
      return obj;
    }
    case SET_EVENT_REMOVE_ERROR:
      return {
        ...state,
        isRemoving: false,
        removeError: true,
        errorMsg: action.errorMsg,
      };

    case SET_EVENTS_SEARCH_PENDING:
      return {
        ...state,
        isSearchingEvents: true,
      };
    case SET_EVENTS_SEARCH_SUCCESS: {
      const stateCopy = { ...state };
      if (
        stateCopy.filteredEvents &&
        stateCopy.filteredEvents.results instanceof Array &&
        action.resp &&
        action.resp.data &&
        action.resp.data.page > 1 &&
        action.resp.data.results instanceof Array
      ) {
        stateCopy.filteredEvents.results =
          stateCopy.filteredEvents.results.concat(action.resp.data.results);

        // Remove any duplicate events that may have crept into the
        // dataset by being pushed across page boundaries
        stateCopy.filteredEvents.results = _uniqBy(
          stateCopy.filteredEvents.results,
          'event_id',
        );

        // Cap accumulated results to prevent unbounded memory growth
        if (stateCopy.filteredEvents.results.length > MAX_ACCUMULATED_EVENTS) {
          stateCopy.filteredEvents.results =
            stateCopy.filteredEvents.results.slice(-MAX_ACCUMULATED_EVENTS);
        }
      } else {
        stateCopy.filteredEvents = action.resp.data;
      }

      if (stateCopy.filteredEvents) {
        stateCopy.filteredEvents.next_page = action.resp.data.next_page;
        stateCopy.filteredEvents.page = action.resp.data.page;
      }
      stateCopy.isSearchingEvents = false;
      stateCopy.searchEventsError = false;
      stateCopy.errorMsg = null;
      return stateCopy;
    }
    case SET_EVENTS_SEARCH_ERROR:
      return {
        ...state,
        isSearchingEvents: false,
        searchEventsError: true,
        errorMsg: action.errorMsg,
        filteredEvents: false,
      };

    case CLEAR_EVENTS_SEARCH:
      return {
        ...state,
        isSearchingEvents: false,
        searchEventsError: false,
        errorMsg: null,
        filteredEvents: false,
      };

    default:
      return state;
  }
};

export default reducer;
